import { useEffect } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  fieldId,
  humanize,
  isFieldVisible,
  normalizeFieldOptions,
  serviceAnswersWithDefaults,
} from '@/lib/documentServices';

const inputClass = 'border-[rgba(6,43,26,0.16)] focus-visible:border-[#D4AF37] focus-visible:ring-[3px] focus-visible:ring-[#D4AF37]/25';

function Help({ field, error, id }) {
  return (
    <>
      {(field.help_text || field.description || field.hint) && (
        <p id={`${id}-help`} className="mt-1 text-xs leading-relaxed text-[#062B1A]/50">
          {field.help_text || field.description || field.hint}
        </p>
      )}
      {error && <p id={`${id}-error`} role="alert" className="mt-1 text-xs font-medium text-red-700">{error}</p>}
    </>
  );
}

function AddressField({ id, field, value, onChange, disabled, error }) {
  const address = value && typeof value === 'object' ? value : {};
  const set = (key, next) => onChange({ ...address, [key]: next });
  const describedBy = error ? `${id}-error` : field.help_text ? `${id}-help` : undefined;
  const addressFields = Array.isArray(field.address_fields) ? field.address_fields : [];
  const settings = (key, fallback = {}) => {
    const schema = addressFields.find((item) => item?.key === key) || {};
    return {
      required: Boolean(field.required && (schema.required ?? key !== 'address_2')),
      maxLength: schema.max_length,
      minLength: schema.min_length,
      pattern: schema.pattern,
      autoComplete: schema.autocomplete,
      inputMode: schema.input_mode,
      placeholder: schema.placeholder || fallback.placeholder,
    };
  };
  return (
    <fieldset className="rounded-xl border border-[#062B1A]/10 bg-[#F7F8FA]/60 p-4">
      <legend className="px-1 text-sm font-semibold text-[#062B1A]">
        {field.label || 'Delivery address'}{field.required ? ' *' : ''}
      </legend>
      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input aria-label={`${field.label || 'Address'} line 1`} {...settings('address_1', { placeholder: 'Street address' })} value={address.address_1 || address.line1 || address.address_line_1 || ''} disabled={disabled} aria-describedby={describedBy} onChange={(event) => set('address_1', event.target.value)} className={inputClass} />
        <Input aria-label={`${field.label || 'Address'} line 2`} {...settings('address_2', { placeholder: 'Apartment, suite, unit (optional)' })} value={address.address_2 || address.line2 || address.address_line_2 || ''} disabled={disabled} onChange={(event) => set('address_2', event.target.value)} className={inputClass} />
        <Input aria-label={`${field.label || 'Address'} city`} {...settings('city', { placeholder: 'City' })} value={address.city || ''} disabled={disabled} onChange={(event) => set('city', event.target.value)} className={inputClass} />
        <Input aria-label={`${field.label || 'Address'} province`} {...settings('province', { placeholder: 'Province / state' })} value={address.province || address.region || ''} disabled={disabled} onChange={(event) => set('province', event.target.value)} className={inputClass} />
        <Input aria-label={`${field.label || 'Address'} postal code`} {...settings('postal_code', { placeholder: 'Postal / ZIP code' })} value={address.postal_code || ''} disabled={disabled} onChange={(event) => set('postal_code', event.target.value)} className={inputClass} />
        <Input aria-label={`${field.label || 'Address'} country`} {...settings('country', { placeholder: 'Country' })} value={address.country || ''} disabled={disabled} onChange={(event) => set('country', event.target.value)} className={inputClass} />
      </div>
      <Help field={field} error={error} id={id} />
    </fieldset>
  );
}

function Field({ field, index, value, onChange, error, disabled }) {
  const id = fieldId(field, index);
  const type = String(field.type || field.input_type || 'text').toLowerCase();
  const label = field.label || field.title || humanize(id);
  const options = normalizeFieldOptions(field.options || field.choices || field.values);
  const describedBy = error ? `${id}-error` : (field.help_text || field.description || field.hint) ? `${id}-help` : undefined;
  const common = {
    id,
    name: id,
    disabled,
    required: Boolean(field.required),
    'aria-invalid': Boolean(error),
    'aria-describedby': describedBy,
    minLength: field.min_length,
    maxLength: field.max_length,
    pattern: field.pattern,
    autoComplete: field.autocomplete,
    inputMode: field.input_mode,
  };

  if (type === 'heading' || type === 'section') {
    return (
      <div className="border-b border-[#062B1A]/10 pb-2 pt-3">
        <h3 className="font-semibold text-[#062B1A]">{label}</h3>
        {(field.description || field.help_text) && <p className="mt-1 text-sm text-[#062B1A]/55">{field.description || field.help_text}</p>}
      </div>
    );
  }

  if (type === 'address') {
    return <AddressField id={id} field={field} value={value} onChange={onChange} disabled={disabled} error={error} />;
  }

  if (['checkbox', 'acknowledgement', 'consent', 'boolean'].includes(type)) {
    return (
      <div className={cn('rounded-xl border p-4', error ? 'border-red-300 bg-red-50/50' : 'border-[#062B1A]/10 bg-[#F7F8FA]/60')}>
        <div className="flex items-start gap-3">
          <Checkbox id={id} checked={Boolean(value)} onCheckedChange={(checked) => onChange(checked === true)} disabled={disabled} aria-invalid={Boolean(error)} aria-describedby={describedBy} />
          <Label htmlFor={id} className="cursor-pointer text-sm font-medium leading-relaxed text-[#062B1A]">
            {label}{field.required ? ' *' : ''}
          </Label>
        </div>
        <Help field={field} error={error} id={id} />
      </div>
    );
  }

  if (type === 'radio') {
    return (
      <fieldset>
        <legend className="text-sm font-medium text-[#062B1A]">{label}{field.required ? ' *' : ''}</legend>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          {options.map((option) => (
            <label key={option.value} className={cn('flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2.5 text-sm', String(value) === option.value ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#062B1A]/10 bg-white')}>
              <input type="radio" name={id} value={option.value} checked={String(value || '') === option.value} onChange={() => onChange(option.value)} disabled={disabled} className="accent-[#062B1A]" />
              {option.label}
            </label>
          ))}
        </div>
        <Help field={field} error={error} id={id} />
      </fieldset>
    );
  }

  return (
    <div>
      <Label htmlFor={id} className="text-sm font-medium text-[#062B1A]">{label}{field.required ? ' *' : ''}</Label>
      {type === 'textarea' ? (
        <Textarea {...common} rows={field.rows || 4} value={value || ''} placeholder={field.placeholder || ''} onChange={(event) => onChange(event.target.value)} className={cn('mt-1.5', inputClass)} />
      ) : type === 'select' ? (
        <select {...common} value={value || ''} onChange={(event) => onChange(event.target.value)} className={cn('mt-1.5 flex h-10 w-full rounded-md border bg-white px-3 py-2 text-sm outline-none', inputClass)}>
          <option value="">Select an option</option>
          {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      ) : (
        <Input
          {...common}
          type={type === 'phone' ? 'tel' : ['email', 'tel', 'date', 'number'].includes(type) ? type : field.format === 'email' ? 'email' : 'text'}
          min={field.min}
          max={field.max}
          value={value || ''}
          placeholder={field.placeholder || ''}
          onChange={(event) => onChange(type === 'number' ? event.target.valueAsNumber || '' : event.target.value)}
          className={cn('mt-1.5', inputClass)}
        />
      )}
      <Help field={field} error={error} id={id} />
    </div>
  );
}

export default function ServiceFormRenderer({ fields = [], answers = {}, onChange, errors = {}, variant, disabled = false }) {
  const visible = fields.map((field, index) => ({ field, index })).filter(({ field }) => isFieldVisible(field, answers, variant));
  useEffect(() => {
    if (disabled) return;
    const next = serviceAnswersWithDefaults(fields, answers, variant);
    if (next !== answers) onChange(next);
  }, [answers, disabled, fields, onChange, variant]);
  if (!visible.length) return null;
  return (
    <div className="space-y-5">
      {visible.map(({ field, index }) => {
        const id = fieldId(field, index);
        return (
          <Field
            key={id}
            field={field}
            index={index}
            value={answers[id]}
            onChange={(value) => onChange({ ...answers, [id]: value })}
            error={errors[id]}
            disabled={disabled}
          />
        );
      })}
    </div>
  );
}
